// ============================================================
// ROLES & PERMISSIONS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { Lock, Plus, Edit2 } from 'lucide-react';
import { Table, Badge, Button, Modal, StatCard } from '../components/ui';
import { getRoles } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { toast } from 'react-toastify';

const permissionGroups = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'users', label: 'User Management' },
  { key: 'restaurants', label: 'Restaurant Management' },
  { key: 'orders', label: 'Orders & Transactions' },
  { key: 'finance', label: 'Finance & Reports' },
  { key: 'support', label: 'Support Tickets' },
  { key: 'settings', label: 'System Settings' },
  { key: 'roles', label: 'Roles & Permissions' },
];

const rolePermissions = {
  'Super Admin': ['dashboard', 'users', 'restaurants', 'orders', 'finance', 'support', 'settings', 'roles'],
  'Admin': ['dashboard', 'users', 'restaurants', 'orders', 'finance', 'support'],
  'Finance Manager': ['dashboard', 'finance'],
  'Restaurant Manager': ['dashboard', 'restaurants', 'orders'],
  'Support Executive': ['dashboard', 'support'],
  'Operations Manager': ['dashboard', 'orders', 'restaurants'],
};

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRole, setEditRole] = useState(null);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    adminApi.getRoles(getRoles)
      .then(res => { setRoles(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(() => { setRoles(getRoles()); setLoading(false); });
  }, []);

  const openEdit = (role) => {
    setEditRole(role);
    const perms = {};
    permissionGroups.forEach(p => {
      perms[p.key] = (rolePermissions[role.name] || []).includes(p.key);
    });
    setPermissions(perms);
  };

  const columns = [
    { key: 'name', label: 'Role', render: v => <b style={{ color: '#E85D04' }}>{v}</b> },
    { key: 'users', label: 'Assigned Users', render: v => <span style={{ fontWeight: 700 }}>{v}</span> },
    { key: 'permissions', label: 'Access Level', render: v => <span className="admin-tag">{v}</span> },
    { key: 'description', label: 'Description' },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <Button variant="secondary" size="sm" icon={<Edit2 size={12} />} onClick={() => openEdit(row)}>
          Edit Permissions
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Roles & Permissions</h1>
          <p className="admin-page-subtitle">Configure role-based access control</p>
        </div>
        <Button variant="primary" icon={<Plus size={14} />} onClick={() => toast.info('Create role coming soon')}>Create Role</Button>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Roles" value={roles.length} icon={<Lock size={18} />} color="#8B5CF6" />
        <StatCard label="Total Admins" value={roles.reduce((s, r) => s + r.users, 0)} icon={<Lock size={18} />} color="#3B82F6" />
        <StatCard label="Permission Groups" value={permissionGroups.length} icon={<Lock size={18} />} color="#E85D04" />
      </div>

      <div className="admin-table-wrapper">
        <Table columns={columns} data={roles} loading={loading} />
      </div>

      {/* Permission Matrix */}
      <div className="admin-card" style={{ marginTop: 24 }}>
        <div className="admin-card-header"><h3 className="admin-card-title">🔒 Permission Matrix</h3></div>
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Module</th>
                {roles.map(r => <th key={r.name}>{r.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {permissionGroups.map(pg => (
                <tr key={pg.key}>
                  <td><b>{pg.label}</b></td>
                  {roles.map(r => (
                    <td key={r.name} style={{ textAlign: 'center' }}>
                      {(rolePermissions[r.name] || []).includes(pg.key) ? (
                        <span style={{ color: '#10B981', fontSize: 18 }}>✓</span>
                      ) : (
                        <span style={{ color: '#D1D5DB', fontSize: 18 }}>✗</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Role Modal */}
      <Modal open={!!editRole} onClose={() => setEditRole(null)} title={`Edit Permissions: ${editRole?.name}`} size="md"
        footer={<>
          <Button variant="secondary" onClick={() => setEditRole(null)}>Cancel</Button>
          <Button variant="primary" onClick={() => { toast.success('Permissions saved!'); setEditRole(null); }}>Save Changes</Button>
        </>}>
        {editRole && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--admin-text-secondary)', marginBottom: 16 }}>
              Configure module access for the <b>{editRole.name}</b> role.
            </p>
            {permissionGroups.map(pg => (
              <div key={pg.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--admin-border)' }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{pg.label}</span>
                <label className="admin-toggle">
                  <input type="checkbox" checked={permissions[pg.key] || false} onChange={e => setPermissions(p => ({ ...p, [pg.key]: e.target.checked }))} />
                  <span className="admin-toggle-slider" />
                </label>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RolesPermissions;
